import { useState, useEffect, useCallback } from "react";
import { useSidebar } from "../../hooks/useSidebar";
import { useAuth } from "../../context/AuthContext";
import { apiFetch } from "../../services/apiClient";
import dayjs, { Dayjs } from "dayjs";
import { ahoraChile } from "../../utils/fechaChile";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import {
  CADUCIDAD_FECHAS_DIAS,
  deserializarDia,
  serializarDia,
} from "../../hooks/filtrosPersistidos";

/**
 * Estado único de un arriendo en el dashboard. Las definiciones y la
 * precedencia (mutuamente excluyente) viven en `docs/modulo-home.md` §2
 * y son la fuente de verdad. Round 2026-07-01: antes eran categorías
 * independientes (multi-etiqueta); ahora cada arriendo tiene 1 solo estado.
 */
export type RentalCategory =
  | "active"
  | "upcoming"
  | "ending-soon"
  | "completed";

export interface RentalSummary {
  id: number;
  propertyId: number | null;
  propertyName: string;
  location: string;
  guest: string;
  platform: string;
  startDate: string;
  endDate: string;
  nights: number;
  /**
   * Estado ÚNICO del arriendo (mutuamente excluyente). El backend lo
   * calcula por precedencia en `home.controller.js` `getResumen`. Ver
   * `docs/modulo-home.md` §2.
   */
  category: RentalCategory;
  price: number;
}

export interface RecentBooking {
  id: number;
  propertyId: number | null;
  propertyName: string;
  guest: string;
  platform: string;
  startDate: string;
  endDate: string;
  nights: number;
  price: number;
  createdAt: string;
}

export interface Totals {
  activos: number;
  porLlegar: number;
  porTerminar: number;
  terminados: number;
}

/** El rango del dashboard: los dos extremos, o nada (picker vacío). */
type RangoHome = [Dayjs, Dayjs] | null;

const serializarRangoHome = (valor: RangoHome): unknown =>
  valor ? [serializarDia(valor[0]), serializarDia(valor[1])] : null;

/**
 * Se descarta lo guardado si no trae las DOS fechas: con el rango vacío el
 * dashboard no consulta nada y aparece en blanco, sin spinner ni explicación.
 * En ese caso vuelve al default (el mes actual).
 */
const deserializarRangoHome = (crudo: unknown): RangoHome | undefined => {
  if (!Array.isArray(crudo) || crudo.length !== 2) return undefined;
  const desde = deserializarDia(crudo[0]);
  const hasta = deserializarDia(crudo[1]);
  if (!desde || !hasta) return undefined;
  return [desde, hasta];
};

const CATEGORIAS: RentalCategory[] = [
  "active",
  "upcoming",
  "ending-soon",
  "completed",
];

export const useHomeLogic = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { token } = useAuth();

  // Default date range: current month.
  // Se recuerda el último rango elegido (round 2026-09-24), pero caduca a los
  // 7 días: el default es «el mes actual» y un mes viejo restaurado muestra un
  // dashboard vencido que se ve exactamente igual al de hoy.
  const [dateRange, setDateRange] = useFiltroPersistido<RangoHome>(
    "home.rango",
    () => [ahoraChile().startOf("month"), ahoraChile().endOf("month")],
    {
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      serializar: serializarRangoHome,
      deserializar: deserializarRangoHome,
    },
  );

  const [rentals, setRentals] = useState<RentalSummary[]>([]);
  const [totals, setTotals] = useState<Totals>({
    activos: 0,
    porLlegar: 0,
    porTerminar: 0,
    terminados: 0,
  });
  const [recentBookings, setRecentBookings] = useState<RecentBooking[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [loading, setLoading] = useState(false);
  // El filtro de las cards. Va junto con el rango: cambiar el rango lo suelta
  // (más abajo), así que se guardan los dos o quedarían contradiciéndose.
  const [statusFilter, setStatusFilter] = useFiltroPersistido<RentalCategory | null>(
    "home.estado",
    null,
    {
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      validar: (v) => v === null || CATEGORIAS.includes(v as RentalCategory),
    },
  );

  const fetchResumen = useCallback(async () => {
    if (!dateRange || !token) return;

    const [desde, hasta] = dateRange;
    setLoading(true);

    try {
      const params = new URLSearchParams({
        desde: desde.format("YYYY-MM-DD"),
        hasta: hasta.format("YYYY-MM-DD"),
      });

      const res = await apiFetch<{ arriendos: RentalSummary[]; totals: Totals }>(
        `/home/resumen?${params}`
      );

      setRentals(res?.arriendos ?? []);
      setTotals(
        res?.totals ?? { activos: 0, porLlegar: 0, porTerminar: 0, terminados: 0 }
      );
    } catch (err) {
      console.error("Error fetching resumen:", err);
      setRentals([]);
      setTotals({ activos: 0, porLlegar: 0, porTerminar: 0, terminados: 0 });
    } finally {
      setLoading(false);
    }
  }, [dateRange, token]);

  const fetchRecientes = useCallback(async () => {
    if (!token) return;
    setLoadingRecent(true);
    try {
      const res = await apiFetch<{ data: RecentBooking[] }>("/home/recientes");
      setRecentBookings(res?.data ?? []);
    } catch (err) {
      console.error("Error fetching recientes:", err);
      setRecentBookings([]);
    } finally {
      setLoadingRecent(false);
    }
  }, [token]);

  useEffect(() => {
    fetchResumen();
  }, [fetchResumen]);

  useEffect(() => {
    fetchRecientes();
  }, [fetchRecientes]);

  const handleDateRangeChange = (
    dates: [Dayjs, Dayjs] | null
  ) => {
    setDateRange(dates);
    setStatusFilter(null);
  };

  // Estado único (mutuamente excluyente): filtramos por igualdad.
  const filteredRentals = statusFilter
    ? rentals.filter((r) => r.category === statusFilter)
    : rentals;

  const toggleStatusFilter = (status: RentalCategory) => {
    setStatusFilter((prev) => (prev === status ? null : status));
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("DD-MM-YYYY");
  };

  return {
    sidebarOpen,
    toggleSidebar,
    dateRange,
    handleDateRangeChange,
    rentals,
    filteredRentals,
    totals,
    loading,
    statusFilter,
    toggleStatusFilter,
    recentBookings,
    loadingRecent,
    formatDate,
  };
};
